import { Extension, type Range, type Editor } from '@tiptap/core';
import {
  Plugin,
  PluginKey,
  TextSelection,
  type Transaction,
} from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

import { editorEventBus } from '../utils/event';

import type { Node as PMNode } from '@tiptap/pm/model';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    searchAndReplace: {
      setSearchTerm: (searchTerm: string) => ReturnType;
      setReplaceTerm: (replaceTerm: string) => ReturnType;
      setCaseSensitive: (caseSensitive: boolean) => ReturnType;
      resetIndex: () => ReturnType;
      nextSearchResult: () => ReturnType;
      previousSearchResult: () => ReturnType;
      focusSearchResult: (options: {
        searchTerm: string;
        occurrence?: number;
        caseSensitive?: boolean;
        clearAfterMs?: number;
      }) => ReturnType;
      replace: () => ReturnType;
      replaceAll: () => ReturnType;
    };
  }
}

export interface SearchAndReplaceOptions {
  searchResultClass: string;
  disableRegex: boolean;
}

export type SearchHighlightMode = 'all' | 'current';

export interface SearchAndReplaceStorage {
  searchTerm: string;
  replaceTerm: string;
  results: Range[];
  resultsDoc?: PMNode | null;
  resultsSearchTerm?: string | null;
  resultsCaseSensitive?: boolean | null;
  lastSearchTerm: string;
  caseSensitive: boolean;
  lastCaseSensitive: boolean;
  resultIndex: number;
  lastResultIndex: number;
  highlightMode: SearchHighlightMode;
  isFocusHighlightActive: boolean;
  focusClearTimer?: ReturnType<typeof setTimeout> | null;
  pendingSearchTimer?: ReturnType<typeof setTimeout> | null;
}

interface TextNodesWithPosition {
  text: string;
  pos: number;
}

function getStorage(editor: Editor): SearchAndReplaceStorage {
  return (editor.storage as any).searchAndReplace;
}

function dispatchUpdate(tr: Transaction, dispatch?: (tr: Transaction) => void) {
  if (!dispatch) return true;
  dispatch(tr.setMeta('addToHistory', false));
  return true;
}

function getRegex(
  s: string,
  disableRegex: boolean,
  caseSensitive: boolean
): RegExp {
  return RegExp(
    disableRegex ? s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') : s,
    caseSensitive ? 'gu' : 'gui'
  );
}

function getSafeRegex(
  s: string,
  disableRegex: boolean,
  caseSensitive: boolean
): RegExp | null {
  try {
    return getRegex(s, disableRegex, caseSensitive);
  } catch {
    return null;
  }
}

const SEARCH_REFRESH_META = 'searchAndReplaceRefresh';
const SEARCH_DEBOUNCE_MS = 120;
const SEARCH_FOCUS_HIGHLIGHT_MS = 1000;

function clearPendingSearch(storage: SearchAndReplaceStorage) {
  if (!storage.pendingSearchTimer) return;
  clearTimeout(storage.pendingSearchTimer);
  storage.pendingSearchTimer = null;
}

function clearFocusClearTimer(storage: SearchAndReplaceStorage) {
  if (!storage.focusClearTimer) return;
  clearTimeout(storage.focusClearTimer);
  storage.focusClearTimer = null;
}

function resetFocusHighlightState(storage: SearchAndReplaceStorage) {
  clearFocusClearTimer(storage);
  storage.isFocusHighlightActive = false;
  storage.highlightMode = 'all';
}

function clearFocusedSearch(editor: Editor, storage: SearchAndReplaceStorage) {
  resetFocusHighlightState(storage);
  storage.searchTerm = '';
  storage.results = [];
  storage.resultIndex = 0;
  storage.lastResultIndex = 0;
  storage.resultsDoc = editor.state.doc;
  storage.resultsSearchTerm = '';
  storage.resultsCaseSensitive = storage.caseSensitive;
}

function scheduleFocusedSearchClear(
  editor: Editor,
  storage: SearchAndReplaceStorage,
  clearAfterMs: number
) {
  clearFocusClearTimer(storage);

  storage.focusClearTimer = setTimeout(() => {
    storage.focusClearTimer = null;
    if (editor.isDestroyed || !storage.isFocusHighlightActive) {
      return;
    }

    clearFocusedSearch(editor, storage);
    editor.view.dispatch(editor.state.tr.setMeta(SEARCH_REFRESH_META, true));
  }, clearAfterMs);
}

function scheduleSearchRecompute(
  editor: Editor,
  storage: SearchAndReplaceStorage,
  searchTerm: string,
  disableRegex: boolean,
  caseSensitive: boolean
) {
  clearPendingSearch(storage);
  const scheduledTerm = searchTerm;
  const scheduledCaseSensitive = caseSensitive;

  storage.pendingSearchTimer = setTimeout(() => {
    storage.pendingSearchTimer = null;
    if (editor.isDestroyed) return;
    if (
      storage.searchTerm !== scheduledTerm ||
      storage.caseSensitive !== scheduledCaseSensitive
    )
      return;

    const regex = getSafeRegex(
      scheduledTerm,
      disableRegex,
      scheduledCaseSensitive
    );
    const doc = editor.state.doc;
    storage.results = regex ? findMatches(doc, regex) : [];
    storage.resultsDoc = doc;
    storage.resultsSearchTerm = scheduledTerm;
    storage.resultsCaseSensitive = scheduledCaseSensitive;
    editor.view.dispatch(editor.state.tr.setMeta(SEARCH_REFRESH_META, true));
  }, SEARCH_DEBOUNCE_MS);
}

function findMatches(doc: PMNode, searchTerm: RegExp): Range[] {
  const results: Range[] = [];
  let textNodesWithPosition: TextNodesWithPosition[] = [];
  let index = 0;

  doc?.descendants((node, pos) => {
    if (node.isText) {
      if (textNodesWithPosition[index]) {
        textNodesWithPosition[index] = {
          text: textNodesWithPosition[index].text + node.text,
          pos: textNodesWithPosition[index].pos,
        };
      } else {
        textNodesWithPosition[index] = { text: `${node.text}`, pos };
      }
    } else {
      index += 1;
    }
  });

  textNodesWithPosition = textNodesWithPosition.filter(Boolean);

  for (const element of textNodesWithPosition) {
    const { text, pos } = element;
    const matches = Array.from(text.matchAll(searchTerm));

    for (const m of matches) {
      if (m[0] === '') break;
      if (m.index !== undefined) {
        results.push({ from: pos + m.index, to: pos + m.index + m[0].length });
      }
    }
  }

  return results;
}

function ensureFreshResults(
  editor: Editor,
  storage: SearchAndReplaceStorage,
  disableRegex: boolean
) {
  if (!storage.searchTerm) return;
  const doc = editor.state.doc;
  const isFresh =
    storage.resultsDoc === doc &&
    storage.resultsSearchTerm === storage.searchTerm &&
    storage.resultsCaseSensitive === storage.caseSensitive;
  if (isFresh) return;

  clearPendingSearch(storage);
  const regex = getSafeRegex(
    storage.searchTerm,
    disableRegex,
    storage.caseSensitive
  );
  if (!regex) {
    storage.results = [];
    storage.resultIndex = 0;
    storage.lastResultIndex = 0;
    storage.resultsDoc = doc;
    storage.resultsSearchTerm = storage.searchTerm;
    storage.resultsCaseSensitive = storage.caseSensitive;
    return;
  }

  storage.results = findMatches(doc, regex);
  storage.resultsDoc = doc;
  storage.resultsSearchTerm = storage.searchTerm;
  storage.resultsCaseSensitive = storage.caseSensitive;

  if (!storage.results.length) {
    storage.resultIndex = 0;
    storage.lastResultIndex = 0;
    return;
  }

  storage.resultIndex = Math.min(
    Math.max(storage.resultIndex, 0),
    storage.results.length - 1
  );
}

function buildDecorations(
  doc: PMNode,
  results: Range[],
  searchResultClass: string,
  resultIndex: number,
  highlightMode: SearchHighlightMode
): { decorations: DecorationSet; resultIndex: number } {
  const decorations: Decoration[] = [];
  const safeIndex =
    results.length > 0
      ? Math.min(Math.max(resultIndex, 0), results.length - 1)
      : 0;

  for (let i = 0; i < results.length; i += 1) {
    if (highlightMode === 'current' && i !== safeIndex) {
      continue;
    }

    const r = results[i];
    const className =
      i === safeIndex
        ? `${searchResultClass} ${searchResultClass}-current`
        : searchResultClass;
    decorations.push(Decoration.inline(r.from, r.to, { class: className }));
  }

  return {
    decorations: DecorationSet.create(doc, decorations),
    resultIndex: safeIndex,
  };
}

export const searchAndReplacePluginKey = new PluginKey(
  'searchAndReplacePlugin'
);

export const SearchAndReplace = Extension.create<
  SearchAndReplaceOptions,
  SearchAndReplaceStorage
>({
  name: 'searchAndReplace',

  addOptions() {
    return {
      searchResultClass: 'lb-editor-search-result',
      disableRegex: true,
    };
  },

  addStorage() {
    return {
      searchTerm: '',
      replaceTerm: '',
      results: [] as Range[],
      resultsDoc: null,
      resultsSearchTerm: null,
      resultsCaseSensitive: null,
      lastSearchTerm: '',
      caseSensitive: false,
      lastCaseSensitive: false,
      resultIndex: 0,
      lastResultIndex: 0,
      highlightMode: 'all',
      isFocusHighlightActive: false,
      focusClearTimer: null,
      pendingSearchTimer: null,
    };
  },

  addCommands() {
    const { disableRegex } = this.options;

    return {
      setSearchTerm:
        (searchTerm: string) =>
        ({ editor, tr, dispatch }) => {
          if (!dispatch) return true;
          const storage = getStorage(editor);
          resetFocusHighlightState(storage);
          storage.searchTerm = searchTerm;
          storage.resultIndex = 0;
          return dispatchUpdate(tr, dispatch);
        },
      setReplaceTerm:
        (replaceTerm: string) =>
        ({ editor, dispatch }) => {
          if (!dispatch) return true;
          getStorage(editor).replaceTerm = replaceTerm;
          return true;
        },
      setCaseSensitive:
        (caseSensitive: boolean) =>
        ({ editor, tr, dispatch }) => {
          if (!dispatch) return true;
          const storage = getStorage(editor);
          resetFocusHighlightState(storage);
          storage.caseSensitive = caseSensitive;
          storage.resultIndex = 0;
          return dispatchUpdate(tr, dispatch);
        },
      resetIndex:
        () =>
        ({ editor, tr, dispatch }) => {
          if (!dispatch) return true;
          const storage = getStorage(editor);
          resetFocusHighlightState(storage);
          storage.resultIndex = 0;
          return dispatchUpdate(tr, dispatch);
        },
      nextSearchResult:
        () =>
        ({ editor, tr, dispatch }) => {
          if (!dispatch) return true;
          const s = getStorage(editor);
          if (!s.results.length) return true;
          const nextIndex = s.resultIndex + 1;
          s.resultIndex = s.results[nextIndex] ? nextIndex : 0;
          return dispatchUpdate(tr, dispatch);
        },
      previousSearchResult:
        () =>
        ({ editor, tr, dispatch }) => {
          if (!dispatch) return true;
          const s = getStorage(editor);
          if (!s.results.length) return true;
          const prevIndex = s.resultIndex - 1;
          s.resultIndex = s.results[prevIndex]
            ? prevIndex
            : s.results.length - 1;
          return dispatchUpdate(tr, dispatch);
        },
      focusSearchResult:
        ({
          searchTerm,
          occurrence = 0,
          caseSensitive = false,
          clearAfterMs = SEARCH_FOCUS_HIGHLIGHT_MS,
        }) =>
        ({ editor, tr, dispatch }) => {
          if (!dispatch) return false;

          const storage = getStorage(editor);
          resetFocusHighlightState(storage);
          storage.searchTerm = searchTerm;
          storage.caseSensitive = caseSensitive;
          storage.resultIndex = 0;
          clearPendingSearch(storage);

          ensureFreshResults(editor, storage, disableRegex);

          if (!storage.results.length) {
            clearFocusedSearch(editor, storage);
            dispatchUpdate(tr.setMeta(SEARCH_REFRESH_META, true), dispatch);
            return false;
          }

          const targetIndex = Math.min(
            Math.max(Math.floor(occurrence), 0),
            storage.results.length - 1
          );
          storage.resultIndex = targetIndex;
          storage.lastSearchTerm = searchTerm;
          storage.lastCaseSensitive = caseSensitive;
          storage.lastResultIndex = targetIndex;
          storage.highlightMode = 'current';
          storage.isFocusHighlightActive = true;

          const result = storage.results[targetIndex];
          if (!result) {
            clearFocusedSearch(editor, storage);
            dispatchUpdate(tr.setMeta(SEARCH_REFRESH_META, true), dispatch);
            return false;
          }

          tr.setSelection(TextSelection.create(tr.doc, result.from, result.to));
          tr.scrollIntoView();
          dispatchUpdate(tr.setMeta(SEARCH_REFRESH_META, true), dispatch);

          requestAnimationFrame(() => {
            if (editor.isDestroyed) {
              return;
            }

            try {
              const { node } = editor.view.domAtPos(result.from);
              const element =
                node instanceof HTMLElement ? node : node.parentElement;
              element?.scrollIntoView?.({
                block: 'nearest',
                behavior: 'smooth',
              });
            } catch {}
          });

          if (clearAfterMs <= 0) {
            clearFocusedSearch(editor, storage);
            dispatchUpdate(
              editor.state.tr.setMeta(SEARCH_REFRESH_META, true),
              dispatch
            );
            return true;
          }

          scheduleFocusedSearchClear(editor, storage, clearAfterMs);

          return true;
        },
      replace:
        () =>
        ({ editor, state, dispatch }) => {
          const storage = getStorage(editor);
          ensureFreshResults(editor, storage, disableRegex);
          const { replaceTerm, results, resultIndex } = storage;
          const current = results[resultIndex];
          if (!current || !dispatch) return false;
          dispatch(state.tr.insertText(replaceTerm, current.from, current.to));
          return true;
        },
      replaceAll:
        () =>
        ({ editor, tr, dispatch }) => {
          const storage = getStorage(editor);
          ensureFreshResults(editor, storage, disableRegex);
          const { replaceTerm, results } = storage;
          if (!results.length || !dispatch) return false;

          for (const range of results) {
            const mappedFrom = tr.mapping.map(range.from, 1);
            const mappedTo = tr.mapping.map(range.to, -1);
            tr.insertText(replaceTerm, mappedFrom, mappedTo);
          }

          dispatch(tr);
          return true;
        },
    };
  },

  addKeyboardShortcuts() {
    const editor = this.editor;

    return {
      // eslint-disable-next-line littlebird/naming-convention
      'Mod-f': () => {
        if (getStorage(editor).isFocusHighlightActive) {
          return true;
        }

        editorEventBus.emit('EDITOR_OPEN_SEARCH_BAR', { data: { editor } });
        return true;
      },
    };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const { searchResultClass, disableRegex } = this.options;

    return [
      new Plugin({
        key: searchAndReplacePluginKey,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, oldState) {
            const { doc, docChanged } = tr;
            const s = getStorage(editor);
            const refresh = tr.getMeta(SEARCH_REFRESH_META) === true;

            const searchChanged =
              s.lastSearchTerm !== s.searchTerm ||
              s.lastCaseSensitive !== s.caseSensitive;

            if (
              !refresh &&
              !docChanged &&
              !searchChanged &&
              s.lastResultIndex === s.resultIndex
            )
              return oldState;

            s.lastSearchTerm = s.searchTerm;
            s.lastCaseSensitive = s.caseSensitive;

            if (!s.searchTerm) {
              clearPendingSearch(s);
              resetFocusHighlightState(s);
              s.results = [];
              s.resultsDoc = doc;
              s.resultsSearchTerm = s.searchTerm;
              s.resultsCaseSensitive = s.caseSensitive;
              s.resultIndex = 0;
              s.lastResultIndex = 0;
              return DecorationSet.empty;
            }

            if (refresh) {
              const { decorations, resultIndex } = buildDecorations(
                doc,
                s.results,
                searchResultClass,
                s.resultIndex,
                s.highlightMode
              );

              s.resultIndex = resultIndex;
              s.lastResultIndex = resultIndex;
              return decorations;
            }

            if (searchChanged) {
              resetFocusHighlightState(s);
              s.results = [];
              s.resultsDoc = null;
              s.resultsSearchTerm = null;
              s.resultsCaseSensitive = null;
              s.resultIndex = 0;
              s.lastResultIndex = 0;
              scheduleSearchRecompute(
                editor,
                s,
                s.searchTerm,
                disableRegex,
                s.caseSensitive
              );
              return DecorationSet.empty;
            }

            if (docChanged && !searchChanged) {
              scheduleSearchRecompute(
                editor,
                s,
                s.searchTerm,
                disableRegex,
                s.caseSensitive
              );
              return oldState.map(tr.mapping, doc);
            }

            clearPendingSearch(s);
            const regex = getSafeRegex(
              s.searchTerm,
              disableRegex,
              s.caseSensitive
            );
            if (!regex) {
              resetFocusHighlightState(s);
              s.results = [];
              s.resultsDoc = doc;
              s.resultsSearchTerm = s.searchTerm;
              s.resultsCaseSensitive = s.caseSensitive;
              s.resultIndex = 0;
              s.lastResultIndex = 0;
              return DecorationSet.empty;
            }

            s.results = findMatches(doc, regex);
            s.resultsDoc = doc;
            s.resultsSearchTerm = s.searchTerm;
            s.resultsCaseSensitive = s.caseSensitive;

            const { decorations, resultIndex } = buildDecorations(
              doc,
              s.results,
              searchResultClass,
              s.resultIndex,
              s.highlightMode
            );

            s.resultIndex = resultIndex;
            s.lastResultIndex = resultIndex;
            return decorations;
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },

  onDestroy() {
    const storage = getStorage(this.editor);
    clearPendingSearch(storage);
    clearFocusClearTimer(storage);
  },
});
